import type { SDKRequestFor } from '@st/sdk'
import type { ArchiveGenerationError, FileReadError } from '@st/util/archive/archive-errors'
import type { FileEntry, UploadSelection } from '@st/util/archive/unpack-upload'
import { unpackUpload } from '@st/util/archive/unpack-upload'
import { zipEntries } from '@st/util/archive/zip-entries'
import { Clock, Effect, Stream } from 'effect'
import { BrowserResources, SdkClient, XhrUpload, type BrowserResourceRef } from '~/effects'
import { reportHandledError } from '~/features/error-reporting/report-handled-error'
import { runSdkRequest } from '~/features/module-runtime'

export type FolderImportUploadMessage =
  | {
      type: 'folderImportCreated'
      importId: string
      size: number
    }
  | {
      type: 'folderImportUploadProgressed'
      progress: { bytesTransferred: number; totalBytes: number }
    }
  | { type: 'folderImportSubmitted'; folderImport: { id: string } }
  | { type: 'folderImportFailed' }

type FolderImportUploadRequest = {
  upload: BrowserResourceRef<UploadSelection>
  organizationId: string
}

/**
 * One folder-import upload is one Task stream:
 *
 *   prepare -> create job -> created -> progress* -> submit -> submitted
 *                                  \-> failed <-/
 *
 * The opaque browser input and prepared File stay inside this program. Only
 * domain messages return to the module. `Stream.concat` preserves
 * protocol order: submission cannot start until the upload completes, and an
 * upload failure prevents submission.
 *
 * The upload phase owns this program. Leaving the phase revokes every queued
 * output before interruption, so lifecycle identity never enters its messages.
 */
export function uploadFolderImport({ upload, organizationId }: FolderImportUploadRequest) {
  const failed = () => ({ type: 'folderImportFailed' }) as const

  return Stream.unwrap(
    Effect.gen(function* () {
      const sdk = yield* SdkClient
      const xhrUpload = yield* XhrUpload
      const browserResources = yield* BrowserResources
      const capturedUpload = yield* browserResources.take(upload)
      const now = yield* Clock.currentTimeMillis
      const archiveFile = yield* unpackUpload(capturedUpload).pipe(
        Effect.flatMap((entries) => packToSingleFile(entries, now)),
        Effect.catchTags({
          FileReadError: reportPreparationFailure,
          ArchiveGenerationError: reportPreparationFailure
        })
      )
      if (!archiveFile) return Stream.succeed(failed())

      const create = sdk.send({
        type: 'taxReturnImport/createTaxReturnImport',
        organizationId,
        filename: archiveFile.name,
        fileSize: archiveFile.size
      } satisfies SDKRequestFor<'taxReturnImport/createTaxReturnImport'>)

      const created = yield* runSdkRequest(create)
      if (!created.ok) return Stream.succeed(failed())

      const importId = created.response.id
      const createdMessage = {
        type: 'folderImportCreated',
        importId,
        size: archiveFile.size
      } as const

      const progress = xhrUpload
        .putPresignedUrl({
          url: created.response.uploadUrl,
          headers: created.response.uploadHeaders,
          file: archiveFile
        })
        .pipe(
          Stream.map(
            (progress) =>
              ({
                type: 'folderImportUploadProgressed',
                progress: {
                  bytesTransferred: progress.uploadedBytes,
                  totalBytes: progress.totalBytes
                }
              }) as const
          )
        )

      const submit = Effect.gen(function* () {
        const submitRequest = sdk.send({
          type: 'taxReturnImport/submitTaxReturnImport',
          taxReturnImportId: importId
        } satisfies SDKRequestFor<'taxReturnImport/submitTaxReturnImport'>)

        const submitted = yield* runSdkRequest(submitRequest)
        if (!submitted.ok) return failed()

        return {
          type: 'folderImportSubmitted',
          folderImport: submitted.response
        } as const
      })

      return Stream.concat(
        Stream.succeed(createdMessage),
        Stream.concat(progress, Stream.fromEffect(submit)).pipe(
          Stream.catchTags({
            NetworkError: () => Stream.succeed(failed()),
            HttpError: () => Stream.succeed(failed())
          })
        )
      )
    })
  )
}

function reportPreparationFailure(error: { cause: unknown }) {
  return reportHandledError(error.cause, {
    source: 'folder-import',
    operation: 'prepare-upload'
  }).pipe(Effect.as(null))
}

function packToSingleFile(
  fileEntries: FileEntry[],
  now: number
): Effect.Effect<File, FileReadError | ArchiveGenerationError> {
  if (
    fileEntries.length == 1 &&
    (fileEntries[0].path.endsWith('.zip') || fileEntries[0].path.endsWith('.7z'))
  ) {
    return Effect.succeed(fileEntries[0].file)
  }

  const timePrefix = new Date(now).toISOString().replace(/(\..+$|[:-])/g, '')
  return zipEntries(fileEntries, `import-${timePrefix}.zip`)
}
